import * as React from "react"
import { Container, Row, Col, Spinner, Form, Button } from "react-bootstrap"
import { defaults, HorizontalBar } from "react-chartjs-2"
import "chartjs-plugin-datalabels"
import { ChartData, ChartOptions } from "chart.js"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import PoolStatisticsDetailsGet from "../../../api/Randoms/PoolStatisticsDetailsGet"
import PoolStatisticsDetails from "../../../api/Randoms/RandomsModel/PoolStatisticsDetails"
import TestingPeriodBreakdownGrid from "./TestingPeriodBreakdownGrid"
import PoolCriteria, { GetCompanyPoolCriteriaItem, GetPoolCriteria } from "../../../api/Randoms/RandomsModel/PoolCriteria"
import { ExcusedReport, ExcusedReportGenerate } from "../../../api/Reports/WorkforceReport"
import FormatError from "../../../formatters/FormatError"
import { toast } from "react-toastify"
import printPdf from "../../../utils/printPdf"
import AuditPoolModal from "./AuditPoolModal"
import PoolStatisticsTestsGrid from "./PoolStatisticsTestsGrid"

interface PoolStatisticsProps {
  poolId: number
  poolProgramId: number
  poolName: string
  isConsortium: boolean
  currentCompany: number
  allowExcusedReport: boolean
  allowAuditReport: boolean
  unmaskDonorId: number
}

interface PoolStatisticsState {
  statisticsLoading: boolean
  criteriaLoading: boolean
  statistics?: PoolStatisticsDetails
  poolCriteria?: PoolCriteria
  companies: {ID: number, Name: string, CompanyName: string, CompanyCode: string}[]
  selectedCompany: number
  showAuditPoolOptions: boolean
  showCompletedModal: boolean
  showPendingModal: boolean
}

let labels = [
  "ESTIMATED TESTS REQUIRED",
  "TOTAL TESTS COMPLETED",
  "TOTAL TESTS PENDING COLLECTION",
]

const options: ChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      right: 50
    }
  },
  legend: {
    position: 'bottom',
    labels: {
      usePointStyle: true,
      boxWidth: 10,
      fontSize: 16
    }
  },
  plugins: {
    datalabels: {
      color: '#000',
      display: true
    }
  },
  scales: {
    yAxes: [{ ticks: { callback: (value: string) => { return labels.includes(value) ? "" : value }, fontSize: 16 } }],
    xAxes: [{ ticks: { min: 0 } }]
  }
}

defaults.global.defaultFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
defaults.global.defaultFontColor = '#212529'

export default class PoolStatistics extends React.PureComponent<PoolStatisticsProps, PoolStatisticsState> {
  state: PoolStatisticsState = {
    statisticsLoading: false,
    criteriaLoading: false,
    companies: [],
    selectedCompany: this.props.currentCompany,
    showAuditPoolOptions: false,
    showCompletedModal: false,
    showPendingModal: false,
  }

  subscriptions = new Subscription()
  company$ = new Subject<number>()

  componentDidMount() {
    this.subscriptions.add(this.company$.pipe(
      switchMap(company => concat(
        of({ statisticsLoading: true }),
        PoolStatisticsDetailsGet(this.props.poolId, this.props.poolProgramId, company).pipe(
          map(result => ({ statisticsLoading: false, result: result })),
          catchError(() => of({ statisticsLoading: false }))
        )
      ))
    ).subscribe(
      (data: { statisticsLoading: boolean, result?: PoolStatisticsDetails }) => {
        this.setState({
          statisticsLoading: data.statisticsLoading,
          statistics: data.result
        })
      }))

    if (this.props.isConsortium) {
      this.setState({ criteriaLoading: true })
      this.subscriptions.add(GetPoolCriteria(this.props.poolId).subscribe(
          (data: PoolCriteria) => {
            const companyItem = GetCompanyPoolCriteriaItem(data)
            const companies = companyItem.Values.map(value => {return {ID: value.IntValue, Name: value.StringValue,  CompanyName: value.StringValue2 || "", CompanyCode: value.StringValue3 || ""}})
            this.setState({ poolCriteria: data, companies: companies, criteriaLoading: false })
          },
          () => this.setState({ criteriaLoading: false })
        )
      )
    }

    this.setState({ statisticsLoading: true })
    this.company$.next(this.props.currentCompany)
  }

  componentWillUnmount() {
    this.subscriptions && this.subscriptions.unsubscribe()
  }

  onCompanySelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const accountID = Number(event.target.value)
    this.setState({selectedCompany: accountID})
    this.company$.next(accountID)
  }

  runExcusedReport = () => {
    const params: ExcusedReport = {
      AccountID: this.state.selectedCompany,
      PoolID: this.props.poolId,
      PoolName: this.props.poolName
    }

    this.subscriptions.add(
      ExcusedReportGenerate(params).subscribe(
        (base64pdf: string) => printPdf(base64pdf),
        (error) => toast.error(FormatError(error))
      )
    )
  }

  render() {
    const { statistics, selectedCompany, companies, showAuditPoolOptions } = this.state

    let dataDrug = [statistics?.TotalTestsRequiredDrug, statistics?.TotalTestsCompletedDrug, statistics?.TotalTestsPendingDrug]
    let dataAlco = [statistics?.TotalTestsRequiredAlcohol, statistics?.TotalTestsCompletedAlcohol, statistics?.TotalTestsPendingAlcohol]
    if (selectedCompany > 0) {
      labels = labels.slice(0,3)
      dataDrug = dataDrug.slice(0,3)
      dataAlco = dataAlco.slice(0,3)
    }
    const data: ChartData = {
      labels: labels,
      datasets: [
        {
          label: 'DRUG',
          backgroundColor: '#F7911E',
          borderColor: '#F7911E',
          borderWidth: 0,
          data: dataDrug,
          datalabels: {
            align: 'end',
            anchor: 'end',
          },
          barPercentage: 1.0,
          categoryPercentage: 0.9
        },
        {
          label: 'ALCOHOL',
          backgroundColor: '#5C5C5C',
          borderColor: '#5C5C5C',
          borderWidth: 0,
          data: dataAlco,
          datalabels: {
            align: 'end',
            anchor: 'end',
          },
          barPercentage: 1.0,
          categoryPercentage: 0.9
        }
      ]
    }

    return <>
      <Container fluid className="grid-container py-0">
      {this.props.allowExcusedReport && !this.state.criteriaLoading && <Row className="form-group pt-4">
          {!!this.props.isConsortium  && this.props.currentCompany == 0 && companies.length > 0 && <Col xs={3} md={4}>
            <Form.Control id="company"
              as="select"
              value={selectedCompany}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.onCompanySelect(e)}>
              <option value="">Select the Company</option>
              {this.state.companies.map(item => <option value={item.ID} key={item.ID}>{item.CompanyName} (Company Code: {item.CompanyCode})</option>)}
            </Form.Control>
          </Col>}
          <Col>
            <Button variant="underline-link" onClick={() => this.runExcusedReport()}>
              Run Excused Report
            </Button>
          </Col>
        </Row>}
        {showAuditPoolOptions && <AuditPoolModal onClose={() => this.setState({showAuditPoolOptions: false})} poolID={this.props.poolId} poolName={this.props.poolName}/>}
        {this.props.allowAuditReport && <Row>
          <Col>
            <Button variant="underline-link" onClick={() => this.setState({ showAuditPoolOptions: true })}>
              Pool Audit Details
            </Button>
          </Col>
        </Row>}
        <Row>
          <Col>
            <TestingPeriodBreakdownGrid showRandomSelectionLink poolId={this.props.poolId} poolProgramId={this.props.poolProgramId} companyId={this.state.selectedCompany} poolName={this.props.poolName} unmaskDonorId={this.props.unmaskDonorId}/>
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>TESTING TOTALS</h5>
            <Container fluid>
              <Row>
                <Col xs={4}>
                  <Row>
                    <Col xs={8} className="p-0">CURRENT POOL</Col>
                    <Col xs={4}>{statistics?.CurrentPoolSize}</Col>
                  </Row>
                </Col>
                <Col xs={4}>
                  <Row>
                    <Col xs={9}>DRUG TEST PROGRAM TARGET</Col>
                    <Col xs={1}>{statistics?.TargetDrug}{statistics?.RateOrFixed === 1 && <span>%</span>}</Col>
                  </Row>
                </Col>
                <Col xs={4}>
                  <Row>
                    <Col xs={9}>ALCOHOL TEST PROGRAM TARGET</Col>
                    <Col xs={1}>{statistics?.TargetAlcohol}{statistics?.RateOrFixed === 1 && <span>%</span>}</Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <Row>
                    <Col xs={8} className="p-0">AVERAGE POOL SIZE</Col>
                    <Col xs={4}>{statistics?.AveragePoolSize}</Col>
                  </Row>
                </Col>
                <Col xs={4}>
                  <Row>
                    <Col xs={9}>DRUG TEST PROGRAM ACTUAL</Col>
                    <Col xs={1}>{statistics?.TotalTestsCompletedDrug}</Col>
                  </Row>
                </Col>
                <Col xs={4}>
                  <Row>
                    <Col xs={9}>ALCOHOL TEST PROGRAM ACTUAL</Col>
                    <Col xs={1}>{statistics?.TotalTestsCompletedAlcohol}</Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
        {this.state.statisticsLoading && <Row><Col className="text-center"><Spinner animation="border" variant="secondary" /></Col></Row>}
        {this.state.statistics &&
          <Row>
            <Col xs={3} className="mt-3">
              <Container fluid style={{ height: "170px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Row className="align-items-center" style={{ flex: 1 }}>
                  <Col className="text-center">ESTIMATED TESTS REQUIRED</Col>
                </Row>
                <Row className="align-items-center" style={{ flex: 1 }}>
                  <Col className="text-center">
                    <Button variant="underline-link pl-1" onClick={() => this.setState({showCompletedModal: true})}>
                      TOTAL TESTS COMPLETED
                    </Button>
                  </Col>
                </Row>
                <Row className="align-items-center" style={{ flex: 1 }}>
                  <Col className="text-center">
                    <Button variant="underline-link pl-1" onClick={() => this.setState({showPendingModal: true})}>
                      TOTAL TESTS PENDING COLLECTION
                    </Button>
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col xs={9}>
              <HorizontalBar data={data} options={options} height={250} />
            </Col>
          </Row>}
      </Container>
      {this.state.showCompletedModal && (
          <PoolStatisticsTestsGrid
            type={"Completed"}
            poolID={this.props.poolId}
            accountID={this.state.selectedCompany}
            onClose={() => this.setState({showCompletedModal: false})}
          />
        )}
        {this.state.showPendingModal && (
          <PoolStatisticsTestsGrid
            type={"Pending"}
            poolID={this.props.poolId}
            accountID={this.state.selectedCompany}
            onClose={() => this.setState({showPendingModal: false})}
          />
        )}
    </>
  }
}
